import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, X, ChevronDown, RotateCcw, Home as HomeIcon, LayoutList, AlertTriangle, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { RichText } from "@/components/RichText";
import { GuestSaveBanner } from "@/components/GuestSaveBanner";
import { CaseRating } from "@/components/CaseRating";

const ratingColor = (r) => ({ "Clear pass": "var(--ok)", Pass: "var(--ok)", Borderline: "var(--warn)" }[r] || "var(--alert)");

const List = ({ items, icon: Icon, color, testid }) => (
  <ul data-testid={testid} className="space-y-1.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2 text-sm"><Icon size={15} className="mt-0.5 shrink-0" style={{ color }} />{t}</li>
    ))}
  </ul>
);

const Label = ({ children, color }) => <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color }}>{children}</p>;

const RUBRIC_ROWS = [["quality_safety", "Quality & safety"], ["rural_context", "Approach to scenario / rural context"], ["structure", "Structured approach & response to change"]];

const RubricScore = ({ label, v, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 text-sm py-1.5">
    <span>{label}</span>
    <span className="font-mono font-bold" style={{ color: v >= 2 ? "var(--ok)" : "var(--alert)" }}>{v ?? "–"}/4</span>
  </div>
);

const RubricCard = ({ r }) => (
  <section data-testid="results-rubric-card" className="card p-5 mt-4">
    <Label color="var(--accent)">ANZCA marking rubric (pass = 2+ in every row)</Label>
    <div className="grid sm:grid-cols-2 gap-x-8 divide-y sm:divide-y-0" style={{ borderColor: "var(--line)" }}>
      <div>{(r.components || []).map((v, k) => <RubricScore key={k} label={`Component ${k + 1}: clinical knowledge`} v={v} testid={`results-rubric-component-${k}`} />)}</div>
      <div>{RUBRIC_ROWS.map(([k, label]) => <RubricScore key={k} label={label} v={r[k]} testid={`results-rubric-${k}`} />)}</div>
    </div>
    {r.comment && <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>{r.comment}</p>}
  </section>
);

const QuestionResult = ({ q, fb, transcript, i }) => {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid={`results-question-${i}`} className="card p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="font-mono text-xs font-bold" style={{ color: "var(--accent)" }}>Q{i + 1} · {q.minutes} MIN</span>
          <h3 className="mt-1 font-display font-semibold leading-snug whitespace-pre-line">{q.prompt}</h3>
        </div>
        <span className="font-mono text-xl font-bold shrink-0">{fb?.score ?? "–"}<span className="text-sm" style={{ color: "var(--ink-3)" }}>/10</span></span>
      </div>
      <button data-testid={`results-model-answer-toggle-${i}`} onClick={() => setOpen((o) => !o)} className="mt-3 text-sm font-medium flex items-center gap-1" style={{ color: "var(--accent)" }}>
        {open ? "Hide" : "Show"} model answer <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`mt-3 grid gap-4 ${open ? "md:grid-cols-2" : ""}`}>
        <div className="flex flex-col">
          <Label color="var(--ink-3)">Your answer (transcript)</Label>
          <p data-testid={`results-transcript-${i}`} className="text-sm whitespace-pre-wrap rounded-lg p-3 flex-1" style={{ background: "var(--muted-bg)", color: "var(--ink-2)" }}>{transcript || "No answer given."}</p>
        </div>
        {open && (
          <div>
            <Label color="var(--accent)">{q.anzca_expectation ? "Trainer model answer (not from ANZCA)" : "Model answer"}</Label>
            <ul data-testid={`results-model-answer-${i}`} className="space-y-2 text-sm rounded-lg p-3 border" style={{ borderColor: "var(--accent)" }}>
              {q.points.map((p, j) => <li key={j}>{p.label && <strong className="font-semibold">{p.label}: </strong>}<RichText text={p.text} /></li>)}
            </ul>
          </div>
        )}
      </div>
      {q.anzca_expectation && (
        <div data-testid={`results-anzca-expectation-${i}`} className="mt-4 rounded-lg p-3 text-sm flex gap-2" style={{ background: "color-mix(in srgb, var(--ok) 10%, transparent)" }}>
          <GraduationCap size={16} className="shrink-0 mt-0.5" style={{ color: "var(--ok)" }} />
          <span><strong className="font-semibold">ANZCA examiner expectation: </strong>{q.anzca_expectation}</span>
        </div>
      )}
      {fb && <FeedbackDetail fb={fb} i={i} />}
    </div>
  );
};

const FeedbackDetail = ({ fb, i }) => (
  <>
    {fb.structure && (
      <div data-testid={`results-structure-${i}`} className="mt-5 rounded-lg p-3 text-sm flex gap-2" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}>
        <LayoutList size={16} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
        <span><strong className="font-semibold">Structure: </strong>{fb.structure}</span>
      </div>
    )}
    {fb.basics_missed?.length > 0 && (
      <div data-testid={`results-basics-missed-${i}`} className="mt-3 rounded-lg p-3" style={{ background: "color-mix(in srgb, var(--warn) 12%, transparent)" }}>
        <Label color="var(--warn)">Obvious basics you didn't say out loud</Label>
        <List items={fb.basics_missed} icon={AlertTriangle} color="var(--warn)" testid={`results-basics-list-${i}`} />
      </div>
    )}
    <div className="mt-4 grid sm:grid-cols-2 gap-4">
      <div><Label color="var(--ok)">Covered</Label><List items={fb.covered} icon={Check} color="var(--ok)" testid={`results-covered-${i}`} /></div>
      <div><Label color="var(--alert)">Missed</Label><List items={fb.missed} icon={X} color="var(--alert)" testid={`results-missed-${i}`} /></div>
    </div>
    {fb.comment && <p className="mt-4 text-sm" style={{ color: "var(--ink-2)" }}>{fb.comment}</p>}
  </>
);

export default function Results() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [c, setC] = useState(null);

  useEffect(() => {
    http.get(`/attempts/${attemptId}`).then((r) => {
      setA(r.data);
      return http.get(`/cases/${r.data.case_id}`);
    }).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [attemptId]);

  if (!a || !c) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;
  const fbBy = Object.fromEntries(a.questions.map((q) => [q.question_index, q]));
  const ans = Object.fromEntries(a.answers.map((x) => [x.question_index, x.transcript]));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <p className="font-mono text-xs" style={{ color: "var(--ink-3)" }}>D{a.domain} · {DOMAIN_SHORT[a.domain - 1]} · {Math.floor(a.elapsed_seconds / 60)}m {a.elapsed_seconds % 60}s</p>
      <h1 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight">{a.case_title}</h1>

      <section data-testid="results-score-card" className="card p-6 mt-6 grid sm:grid-cols-[auto,1fr] gap-6 items-center fade-up">
        <div className="text-center sm:pr-6 sm:border-r" style={{ borderColor: "var(--line)" }}>
          <div data-testid="results-overall-score" className="font-mono text-5xl font-bold">{a.overall_score}<span className="text-2xl">%</span></div>
          <div data-testid="results-global-rating" className="mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ background: ratingColor(a.global_rating) }}>{a.global_rating}</div>
        </div>
        <p data-testid="results-summary" className="text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>{a.summary}</p>
      </section>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="card p-5"><p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--ok)" }}>Strengths</p><List items={a.strengths} icon={Check} color="var(--ok)" testid="results-strengths" /></div>
        <div className="card p-5"><p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--warn)" }}>To improve</p><List items={a.improvements} icon={X} color="var(--warn)" testid="results-improvements" /></div>
      </div>

      {a.structure_feedback && (
        <div data-testid="results-structure-feedback" className="card p-5 mt-4 flex gap-3">
          <LayoutList size={18} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} />
          <div><Label color="var(--accent)">Answer structure</Label><p className="text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{a.structure_feedback}</p></div>
        </div>
      )}

      <CaseRating caseId={c.id} />
      {a.rubric && <RubricCard r={a.rubric} />}
      {a.examiners?.length > 0 && (
        <section data-testid="results-examiners" className="card p-5 mt-4">
          <Label color="var(--accent)">Two independent examiners: safe for independent rural practice?</Label>
          <ul className="space-y-1.5">
            {a.examiners.map((e, k) => (
              <li key={k} data-testid={`results-examiner-${k}`} className="flex gap-2 text-sm">
                {e.safe ? <Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--ok)" }} /> : <X size={15} className="mt-0.5 shrink-0" style={{ color: "var(--alert)" }} />}
                <span><strong className="font-semibold">Examiner {k + 1}: {e.safe ? "Safe" : "Not yet safe"}.</strong> {e.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {c.learning_objectives?.length > 0 && (
        <details data-testid="results-learning-objectives" className="card p-5 mt-4 text-sm">
          <summary className="cursor-pointer font-semibold">ANZCA learning objectives assessed ({c.learning_objectives.length})</summary>
          <ul className="mt-3 space-y-1.5" style={{ color: "var(--ink-2)" }}>{c.learning_objectives.map((l, k) => <li key={k}>{l}</li>)}</ul>
        </details>
      )}

      <div className="mt-8 space-y-4">
        {c.questions.map((q, i) => <QuestionResult key={i} q={q} i={i} fb={fbBy[i]} transcript={ans[i]} />)}
      </div>

      <div className="mt-8"><GuestSaveBanner testid="results-guest-banner" /></div>

      <div className="mt-10 flex flex-wrap gap-3">
        <button data-testid="results-retry-button" onClick={() => nav(`/viva/${c.id}`)} className="btn-ghost h-11 px-5 rounded-full text-sm font-medium flex items-center gap-2"><RotateCcw size={16} /> Retry this case</button>
        <Link to="/" data-testid="results-home-button" className="btn-primary h-11 px-6 rounded-full font-semibold flex items-center gap-2"><HomeIcon size={16} /> New case</Link>
      </div>
    </div>
  );
}
